/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { css } from '@emotion/react';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce, useToggle } from 'react-use';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';

import Loader from 'components/loader/loader';
import { Info } from 'components/shared/Info';
import { UsageTrackingPagination } from './UsageTrackingPagination';
import { UsageTrackingListView } from './UsageTrackingListview';
import { DateRangePicker } from './DateRangePicker';
import { SearchInputField } from './SearchInputField';
import {
  FiltersButton,
  type FilterState,
  type RangeFilterOption,
  FILE_SIZE_FILTERS,
  DURATION_FILTERS,
  COMPUTE_TIME_FILTERS,
} from './FiltersButton';
import { GenerateButton } from './GenerateButton';
import {
  DetailedUsage,
  UsageTrackingService,
} from 'services/usage-tracking.service';
import { CustomDateParams } from 'utils/models';
import { defaultDatesRange } from 'utils/default/defaultSetting';
import { formatSecondsToDuration } from 'utils/usageTracking.util';
import 'components/Library/Listview.scss';
import {
  ColumnResizeMode,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

// ============================================================================
// Types & Constants
// ============================================================================

// ============================================================================
// Types & Constants
// ============================================================================

type DetailedUsageTableProps = {};

const PAGE_SIZE_OPTIONS = [12, 30, 60, 120] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const COLUMN_RESIZE_MODE: ColumnResizeMode = 'onChange';

// ============================================================================
// Helper Functions
// ============================================================================

const createInitialFilters = (): FilterState => ({
  contentTypes: [],
  fileSizes: [],
  durations: [],
  computeTimes: [],
});

const isString = (value: unknown): value is string => typeof value === 'string';

const isWithinRange = (
  value: number,
  option: RangeFilterOption<string>,
): boolean => {
  const minPass =
    option.includeMin === false ? value > option.min : value >= option.min;
  const maxPass =
    typeof option.max === 'number'
      ? option.includeMax === false
        ? value < option.max
        : value <= option.max
      : true;

  return minPass && maxPass;
};

/**
 * Converts a date string to a Date object with only the date portion (no time)
 */
const toDateOnly = (date: Date | string): Date => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Checks if a date falls within the specified date range
 */
const isDateInRange = (
  dateString: string,
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
): boolean => {
  const recordDate = toDateOnly(dateString);

  if (startDate && recordDate < toDateOnly(startDate)) {
    return false;
  }

  if (endDate && recordDate > toDateOnly(endDate)) {
    return false;
  }

  return true;
};

/**
 * Searches through record fields for matching text
 */
const matchesSearchTerm = (
  record: DetailedUsage,
  searchTerm: string,
): boolean => {
  if (!searchTerm.trim()) return true;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  
  // Format duration and compute time the same way they're displayed in the table
  // Use seconds for accurate display (matches summary totals)
  const formattedDuration = formatSecondsToDuration(record.durationSeconds || 0);
  const formattedComputeTime = formatSecondsToDuration(record.computeTimeSeconds || 0);
  
  // Include both formatted display values and raw numeric values for comprehensive search
  const searchableFields = [
    record.apiName,
    record.date,
    record.fileName,
    record.contentType,
    record.fileSize,
    formattedDuration, // Formatted duration as displayed (e.g., "30m 45s")
    formattedComputeTime, // Formatted compute time as displayed
    record.durationMinutes?.toString(), // Raw numeric duration in minutes
    record.computeTimeMinutes?.toString(), // Raw numeric compute time in minutes
  ];

  return searchableFields
    .filter(isString)
    .map((value) => value.toLowerCase())
    .some((value) => value.includes(normalizedSearch));
};

/**
 * Checks if record matches content type filter
 */
const matchesContentTypeFilter = (
  record: DetailedUsage,
  filterTypes: string[],
): boolean => {
  if (filterTypes.length === 0 || filterTypes.length >= 2) {
    return true; // Show all if none selected or both selected
  }

  if (!record.contentCategory) {
    return false; // Exclude records with null contentCategory
  }

  return filterTypes.includes(record.contentCategory);
};

/**
 * Checks if record matches a range filter (file size, duration, compute time)
 */
const matchesRangeFilter = <T extends string>(
  value: number | null,
  filterIds: string[],
  filterOptions: RangeFilterOption<T>[],
): boolean => {
  if (filterIds.length === 0) return true;

  const normalizedValue = value ?? 0;
  return filterIds.some((filterId) => {
    const option = filterOptions.find((config) => config.id === filterId);
    return option ? isWithinRange(normalizedValue, option) : true;
  });
};

// ============================================================================
// Table Column Definitions
// ============================================================================

const createTableColumns = () => [
  {
    header: 'API Name',
    accessorKey: 'apiName',
    size: 150,
    minSize: 120,
    hint: 'Name of the API key used',
    style: { textAlign: 'left' },
    enableSorting: true,
  },
  {
    header: 'Date',
    accessorKey: 'date',
    size: 120,
    minSize: 100,
    hint: 'Date of the API call',
    style: { textAlign: 'center' },
    enableSorting: true,
  },
  {
    header: 'File name / Job ID',
    accessorKey: 'fileName',
    size: 250,
    minSize: 200,
    hint: 'Name of the file or job ID',
    style: { textAlign: 'left' },
    enableSorting: true,
  },
  {
    header: 'Content type',
    accessorKey: 'contentType',
    size: 150,
    minSize: 120,
    hint: 'Type of content (Audio/Video)',
    style: { textAlign: 'center' },
    enableSorting: true,
  },
  {
    header: 'File size',
    accessorKey: 'fileSize',
    size: 120,
    minSize: 100,
    hint: 'Size of the file',
    style: { textAlign: 'center' },
    enableSorting: true,
  },
  {
    header: 'Transcribed duration',
    accessorKey: 'durationSeconds',
    size: 180,
    minSize: 150,
    hint: 'Duration of transcribed content',
    style: { textAlign: 'center' },
    enableSorting: true,
  },
  {
    header: 'Compute time',
    accessorKey: 'computeTimeSeconds',
    size: 150,
    minSize: 120,
    hint: 'Time taken to process',
    style: { textAlign: 'center' },
    enableSorting: true,
  },
];

// ============================================================================
// Component
// ============================================================================

export const DetailedUsageTable = (props: DetailedUsageTableProps) => {
  // ==========================================================================
  // State
  // ==========================================================================

  const apiKeys = useSelector((state: RootState) => state.apiKey.items);

  const [isLoading, toggleLoading] = useToggle(false);
  const [detailedUsages, setDetailedUsages] = useState<DetailedUsage[]>([]);
  const [filteredUsages, setFilteredUsages] = useState<DetailedUsage[]>([]);
  const [filters, setFilters] = useState<FilterState>(createInitialFilters);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<CustomDateParams>(defaultDatesRange);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  // ==========================================================================
  // Memoized Values
  // ==========================================================================

  const columns = useMemo(() => createTableColumns(), []);

  const getApiKeyName = useCallback(
    (apiName: string): string => {
      const apiKey = apiKeys.find((key) => key.name === apiName);
      return apiKey?.name || apiName;
    },
    [apiKeys],
  );

  const activeFilterCount = useMemo(
    () =>
      filters.contentTypes.length +
      filters.fileSizes.length +
      filters.durations.length +
      filters.computeTimes.length,
    [filters],
  );

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const fetchUsageData = useCallback(async () => {
    try {
      setErrorMessage(null);
      toggleLoading(true);
      const response = await UsageTrackingService.getUsageTracking();
      const records = response.data.data;
      setDetailedUsages(records);
    } catch (error) {
      console.error('Error fetching detailed usages:', error);
      setErrorMessage('Unable to load usage records. Please try again later.');
      setDetailedUsages([]);
      setFilteredUsages([]);
    } finally {
      toggleLoading(false);
    }
  }, [toggleLoading]);

  useEffectOnce(() => {
    fetchUsageData();
  });

  // ==========================================================================
  // Filtering Logic
  // ==========================================================================

  const applyFilters = useCallback((): DetailedUsage[] => {
    if (isEmpty(detailedUsages)) {
      return [];
    }

    return detailedUsages.filter((record) => {
      // Search filter
      if (!matchesSearchTerm(record, searchTerm)) {
        return false;
      }

      // Content type filter
      if (!matchesContentTypeFilter(record, filters.contentTypes)) {
        return false;
      }

      // File size filter
      if (
        !matchesRangeFilter(
          record.fileSizeMb,
          filters.fileSizes,
          FILE_SIZE_FILTERS,
        )
      ) {
        return false;
      }

      // Duration filter
      if (
        !matchesRangeFilter(
          record.durationMinutes,
          filters.durations,
          DURATION_FILTERS,
        )
      ) {
        return false;
      }

      // Compute time filter
      if (
        !matchesRangeFilter(
          record.computeTimeMinutes,
          filters.computeTimes,
          COMPUTE_TIME_FILTERS,
        )
      ) {
        return false;
      }

      // Date range filter
      if (
        !isDateInRange(
          record.date,
          dateRange.startDate,
          dateRange.endDate,
        )
      ) {
        return false;
      }

      return true;
    });
  }, [detailedUsages, filters, dateRange, searchTerm]);

  // ==========================================================================
  // Event Handlers
  // ==========================================================================

  const handleGenerate = useCallback(() => {
    const filtered = applyFilters();
    setFilteredUsages(filtered);
    setPage(1);
    setIsFilterOpen(false);
  }, [applyFilters]);

  const handleClearFilters = useCallback(() => {
    // Only reset the filters inside the Filters popup
    // Do NOT reset search term and date range as they are separate controls
    setFilters(createInitialFilters());
    setPage(1);
    // Don't close the filter popup so user can see the changes
    // setIsFilterOpen(false);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Apply filters when Enter is pressed
      const filtered = applyFilters();
      setFilteredUsages(filtered);
      setPage(1);
    }
  }, [applyFilters]);

  const handleDatesChange = useCallback((range: CustomDateParams) => {
    setDateRange(range);
  }, []);

  const toggleFilterValue = useCallback(
    <T extends keyof FilterState>(
      key: T,
      option: FilterState[T][number],
    ) => {
      setFilters((prev) => {
        const selection = prev[key] as typeof option[];
        const exists = selection.includes(option);
        const nextSelection = exists
          ? selection.filter((value) => value !== option)
          : [...selection, option];

        return {
          ...prev,
          [key]: nextSelection,
        };
      });
    },
    [],
  );

  const handleToggleFilterOpen = useCallback(() => {
    setIsFilterOpen((prev) => !prev);
  }, []);

  // ==========================================================================
  // Effects
  // ==========================================================================

  // Initialize filtered data when detailed usages are first loaded
  useEffect(() => {
    if (isEmpty(detailedUsages)) {
      setFilteredUsages([]);
    } else if (isEmpty(filteredUsages)) {
      // Only initialize if filteredUsages is empty (first load)
      // Show all data initially (no filters applied)
      setFilteredUsages(detailedUsages);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedUsages]);

  // Ensure page is valid when filtered results change
  useEffect(() => {
    const totalPages = Math.ceil(filteredUsages.length / pageSize) || 1;
    if (page > totalPages) {
      setPage(1);
    }
  }, [filteredUsages.length, pageSize, page]);

  // ==========================================================================
  // Table Setup
  // ==========================================================================

  const paginatedUsages = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredUsages.slice(start, end);
  }, [filteredUsages, page, pageSize]);

  const table = useReactTable({
    columns,
    data: paginatedUsages,
    state: {
      sorting,
    },
    columnResizeMode: COLUMN_RESIZE_MODE,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  const { rows } = table.getRowModel();

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div tw="flex flex-col mt-2 px-3 md-down:px-2">
      <div tw="flex flex-col space-y-4 mb-4 text-gray-700">
        <div tw="font-bold text-3xl md-down:text-2xl">Detailed usage</div>

        <div
          tw="flex flex-row items-end gap-3 flex-wrap md-down:gap-2"
          css={css`
            & > * {
              display: flex;
              flex-direction: column;
            }
          `}
        >
          <div tw="min-w-[30rem] md-down:min-w-full">
            <SearchInputField
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <FiltersButton
            filters={filters}
            activeFilterCount={activeFilterCount}
            isFilterOpen={isFilterOpen}
            onToggleFilterOpen={handleToggleFilterOpen}
            onToggleFilterValue={toggleFilterValue}
            onClearFilters={handleClearFilters}
          />

          <DateRangePicker
            dateRange={dateRange}
            onDatesChange={handleDatesChange}
          />

          <GenerateButton isLoading={isLoading} onClick={handleGenerate} />
        </div>
      </div>

      {isLoading && (
        <div tw="min-h-[40rem] flex justify-center my-3 items-center shadow rounded bg-white">
          <Loader />
        </div>
      )}

      {!isLoading && (
        <>
          <UsageTrackingListView
            table={table}
            rows={rows}
            tableContainerRef={tableContainerRef}
            getApiKeyName={getApiKeyName}
            isEmpty={isEmpty(filteredUsages)}
          />

          {errorMessage && (
            <div tw="mt-4">
              <Info text={errorMessage} />
            </div>
          )}

          <UsageTrackingPagination
            totalItems={filteredUsages.length}
            pageSize={pageSize}
            page={page}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </div>
  );
};
